import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
	ApSelect,
	ApRadio,
	ApMonthPicker,
	ApLoaderDefault,
	ApLoaderText,
} from '@alixpartners/ui-components';
import {
	Wrapper,
	FormGroup,
	Label,
	DropdownWrapper,
	HorizontalGroup,
	RadioLabel,
	LoadingContainer,
	TimePeriodGroup,
	Description,
	MonthPickerWrapper,
	SelectorWrapper,
} from './InitiativeTypeForm.styles';
import { MONTH_NAMES, getMonthByDate, getYearByDate } from 'utils/date';
import { SPEND_PARAM } from 'Data/constants';
import { getISOString } from 'Settings/utils';
import {
	INITIATIVE_TYPES,
	ITEMS_TYPES,
	ITEMS_TYPE,
	TAIL_SPEND_TYPE,
} from '../../utils';
import { TAXONOMY_TYPES } from 'Investigate/utils';
import ClickOutside from 'react-click-outside';

const TAXONOMY_OPTIONS = [
	{ label: 'Project taxonomy', value: TAXONOMY_TYPES.PROJECT },
	{
		label: 'AP taxonomy',
		value: TAXONOMY_TYPES.AP,
	},
	{
		label: 'Client taxonomy',
		value: TAXONOMY_TYPES.CLIENT,
	},
];

export const InitiativeTypeForm = ({
	defaultDateRange,
	dateRangeOptions,
	initiativeData,
	onInitiativeChange,
	onParamsChange,
}) => {
	const history = useHistory();
	const itemRef = useRef();
	const {
		startDate,
		endDate,
		spendType,
		type,
		initiativeItemType,
		taxonomyType,
	} = initiativeData;

	// Month picker outside click close
	const handleClickOutside = () => {
		const el = itemRef.current;

		if (el) {
			const footerEl = el.getElementsByClassName('ap-button-group')[0];

			if (footerEl) {
				footerEl.getElementsByClassName('ap-button-secondary')[0].click();
			}
		}
	};

	const getMonthPickerRange = () => {
		return {
			from: {
				year: getYearByDate(startDate),
				month: MONTH_NAMES[getMonthByDate(startDate)].toLowerCase(),
				monthNumber: getMonthByDate(startDate) + 1,
			},
			to: {
				year: getYearByDate(endDate),
				month: MONTH_NAMES[getMonthByDate(endDate)].toLowerCase(),
				monthNumber: getMonthByDate(endDate) + 1,
			},
		};
	};

	const getMonthPickerSrcRange = () => {
		const options = dateRangeOptions.filter(item => !!item.year);

		return {
			from: {
				year: options[0].year || getYearByDate(options[0].dateFrom),
				month: getMonthByDate(options[0].dateFrom) + 1,
			},
			to: {
				year: options[options.length - 1].year,
				month: getMonthByDate(options[options.length - 1].dateTo) + 1,
			},
		};
	};

	const handleTimePeriodChange = monthPickerRange => {
		const dateRange = {
			startDate: getISOString(
				monthPickerRange.from.year,
				monthPickerRange.from.monthNumber
			),
			endDate: getISOString(
				monthPickerRange.to.year,
				monthPickerRange.to.monthNumber,
				true
			),
		};

		onParamsChange(dateRange);
	};

	const handleSettingsClick = () => {
		history.push({
			pathname: '/settings',
			hash: 'initiatives_time_period',
		});
	};

	const handleInitiativeTypeChange = type => {
		const { initiativeItemType } = initiativeData;

		// Tail spend has its own initiativeItemType = 5
		// Handle tail spend initiative type change
		if (
			initiativeItemType === ITEMS_TYPE.CATEGORY &&
			type === TAIL_SPEND_TYPE
		) {
			onParamsChange({ initiativeItemType: ITEMS_TYPE.TAIL_SPEND, type });

			return;
		} else if (
			initiativeItemType === ITEMS_TYPE.TAIL_SPEND &&
			type !== TAIL_SPEND_TYPE
		) {
			onParamsChange({ initiativeItemType: ITEMS_TYPE.CATEGORY, type });

			return;
		}

		onInitiativeChange({ type });
	};

	const handleSpendTypeChange = ({ target: { value } }) => {
		const spendParam = Number(value);
		const options = { spendType: spendParam };

		// Handle tail spend switch to direct
		if (
			spendParam === SPEND_PARAM.DIRECT &&
			initiativeItemType === ITEMS_TYPE.TAIL_SPEND
		) {
			options.initiativeItemType = ITEMS_TYPE.CATEGORY;
			options.type = INITIATIVE_TYPES[ITEMS_TYPE.CATEGORY][0].value;
		}

		if (spendType == null) {
			options.taxonomyType = TAXONOMY_TYPES.PROJECT;
		} else if (
			// Handle Ap taxonomy if spend type changed to direct
			spendParam === SPEND_PARAM.DIRECT &&
			taxonomyType === TAXONOMY_TYPES.AP
		) {
			options.taxonomyType = TAXONOMY_TYPES.PROJECT;
		}

		onParamsChange(options);
	};

	const handleTaxonomyChange = value => {
		const taxonomyParam = Number(value);
		const options = { taxonomyType: taxonomyParam };

		onParamsChange(options);
	};

	const getInitiativeTypes = type => {
		const itemType = type != null ? type : initiativeItemType;

		if (itemType == null) {
			return [];
		}

		if (itemType === ITEMS_TYPE.CATEGORY && spendType === SPEND_PARAM.DIRECT) {
			return INITIATIVE_TYPES[itemType].slice(0, -1);
		}

		return INITIATIVE_TYPES[itemType];
	};

	const initiativeTypes = getInitiativeTypes();

	const handleItemsTypeChange = ({ target: { value } }) => {
		const typeValue = Number(value);
		const types = getInitiativeTypes(typeValue);

		onParamsChange({
			initiativeItemType: typeValue,
			type: types && types.length > 0 ? types[0].value : '',
		});
	};

	const getTaxonomyOptions = () => {
		if (spendType === 0) {
			const directOptions = [...TAXONOMY_OPTIONS];

			directOptions.splice(1, 1);

			return directOptions;
		}

		if (spendType === 1) {
			return TAXONOMY_OPTIONS;
		}

		return [];
	};

	if (!startDate || !endDate || !defaultDateRange || !dateRangeOptions) {
		return (
			<LoadingContainer>
				<ApLoaderDefault loaderText={() => <ApLoaderText />} />
			</LoadingContainer>
		);
	}

	return (
		<Wrapper>
			<TimePeriodGroup>
				<Label>Initiative time period</Label>
				<MonthPickerWrapper innerRef={itemRef}>
					<ClickOutside onClickOutside={handleClickOutside}>
						<ApMonthPicker
							onConfirm={handleTimePeriodChange}
							srcRange={getMonthPickerSrcRange()}
							stateRange={getMonthPickerRange()}
						/>
					</ClickOutside>
				</MonthPickerWrapper>
				<Description>
					Default time period is - {defaultDateRange.name}. Default time period
					can be changed in <span onClick={handleSettingsClick}>settings</span>{' '}
					for all future initiatives or set up individually for each initiative.
				</Description>
			</TimePeriodGroup>
			<FormGroup>
				<Label>Please select spend type:</Label>
				<HorizontalGroup>
					<ApRadio
						name="spendType"
						value={0}
						onChange={handleSpendTypeChange}
						checked={spendType === 0}
					>
						<RadioLabel>Direct spend</RadioLabel>
					</ApRadio>
					<ApRadio
						name="spendType"
						value={1}
						onChange={handleSpendTypeChange}
						checked={spendType === 1}
					>
						<RadioLabel>Indirect spend</RadioLabel>
					</ApRadio>
					<SelectorWrapper>
						<ApSelect
							placeholder="-"
							options={getTaxonomyOptions()}
							value={taxonomyType || ''}
							disabled={spendType == null}
							onChange={handleTaxonomyChange}
						/>
					</SelectorWrapper>
				</HorizontalGroup>
			</FormGroup>
			<FormGroup>
				<Label>
					Choose what opportunity items would you like to add into your
					initiative
				</Label>
				<HorizontalGroup>
					{ITEMS_TYPES.map(({ value, values, label, disabled }) => (
						<ApRadio
							key={value}
							name="initiativeItemType"
							value={value}
							onChange={handleItemsTypeChange}
							checked={
								value === initiativeItemType ||
								(!!values && values.includes(initiativeItemType))
							}
							disabled={disabled}
						>
							<RadioLabel disabled={disabled}>{label}</RadioLabel>
						</ApRadio>
					))}
				</HorizontalGroup>
			</FormGroup>
			<FormGroup>
				<Label>Initiative type</Label>
				<DropdownWrapper>
					<ApSelect
						placeholder="None"
						options={initiativeTypes || []}
						value={type || ''}
						onChange={handleInitiativeTypeChange}
						disabled={
							!initiativeItemType ||
							(initiativeTypes && initiativeTypes.length === 1)
						}
					/>
				</DropdownWrapper>
				{initiativeItemType === ITEMS_TYPE.TAIL_SPEND && (
					<Description>
						Select ‘Next’ to review and edit tail settings.
					</Description>
				)}
			</FormGroup>
		</Wrapper>
	);
};

export default InitiativeTypeForm;
